import React, { useState, useRef } from "react";
import {
  Box,
  TextField,
  Popover,
  Chip,
  Typography,
  InputAdornment,
  IconButton,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import {
  subDays,
  subMonths,
  subYears,
  format,
} from "date-fns";

const getShortcuts = () => [
  { label: "Today", date: new Date() },
  { label: "Yesterday", date: subDays(new Date(), 1) },
  { label: "Previous Week", date: subDays(new Date(), 7) },
  { label: "Previous Month", date: subMonths(new Date(), 1) },
  { label: "Previous Quarter", date: subMonths(new Date(), 3) },
  { label: "Previous Year", date: subYears(new Date(), 1) },
];

const formatDisplay = (date) => {
  if (!date) return "";
  return format(new Date(date), "d MMM, yy");
};

const SingleDatePicker = ({ value, onChange }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const inputRef = useRef(null);
  const open = Boolean(anchorEl);

  const handleOpen = (e) => {
    setAnchorEl(e.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleChange = (date) => {
    onChange(date);
    handleClose(); // Close on selecting from picker too? Or maybe let user close it.
  };

  const handleShortcut = (shortcut) => {
    onChange(shortcut.date);
    handleClose();
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      {/* Display field */}
      <TextField
        ref={inputRef}
        fullWidth
        size="medium"
        label="Reference Date"
        value={formatDisplay(value)}
        onClick={handleOpen}
        InputProps={{
          readOnly: true,
          endAdornment: (
            <InputAdornment position="end">
              <IconButton size="small" onClick={handleOpen}>
                <CalendarMonthIcon sx={{ fontSize: "1.4rem", color: "#666" }} />
              </IconButton>
            </InputAdornment>
          ),
          sx: { cursor: "pointer" },
        }}
        sx={{
          "& .MuiInputBase-input": {
            cursor: "pointer",
            fontSize: "1rem",
          },
        }}
      />

      {/* Popover with shortcuts + picker */}
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{
          paper: {
            sx: {
              mt: 1,
              borderRadius: 2,
              boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
              overflow: "hidden",
            },
          },
        }}
      >
        <Box sx={{ display: "flex", minWidth: 400 }}>
          {/* Shortcuts panel */}
          <Box
            sx={{
              width: 160,
              borderRight: "1px solid #eee",
              py: 1.5,
              px: 1,
              maxHeight: 380,
              overflowY: "auto",
              bgcolor: "#fafafa",
            }}
          >
            <Typography
              variant="caption"
              sx={{
                px: 1,
                pb: 0.5,
                display: "block",
                fontWeight: 700,
                color: "#888",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                fontSize: "0.6rem",
              }}
            >
              Quick Select
            </Typography>
            {getShortcuts().map((s) => (
              <Chip
                key={s.label}
                label={s.label}
                size="small"
                onClick={() => handleShortcut(s)}
                sx={{
                  m: 0.3,
                  fontSize: "0.72rem",
                  height: 26,
                  cursor: "pointer",
                  bgcolor: "#fff",
                  border: "1px solid #e0e0e0",
                  fontWeight: 500,
                  "&:hover": {
                    bgcolor: "rgba(94, 79, 170, 0.08)",
                    borderColor: "rgba(94, 79, 170, 0.5)",
                  },
                }}
              />
            ))}
          </Box>

          {/* Date picker */}
          <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 2 }}>
            <Typography
              variant="caption"
              sx={{
                fontWeight: 700,
                color: "#888",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                fontSize: "0.6rem",
              }}
            >
              Custom Date
            </Typography>
            <DatePicker
              label="Select Date"
              value={value}
              onChange={(date) => onChange(date)}
              maxDate={new Date()}
              slotProps={{
                textField: { size: "small", fullWidth: true },
              }}
            />
          </Box>
        </Box>
      </Popover>
    </LocalizationProvider>
  );
};

export default SingleDatePicker;
